import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Icon } from "@/components/Icon";
import { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const schema = z.object({
  email: z.string().email("Ingresa un correo válido"),
  password: z.string().min(1, "La contraseña es obligatoria"),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "admin@amelia.am", password: "" },
  });

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    setSubmitting(true);
    try {
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (err) {
      setServerError(extractApiError(err, "Correo o contraseña incorrectos"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login">
      {/* Lado izquierdo — marca + radar */}
      <div className="login__hero">
        <div className="login__hero-grid" />
        <div className="login__hero-radar">
          <div className="login__hero-radar-sweep" />
        </div>

        <div className="login__brand">
          <img className="login__wordmark" src="/assets/logo-amelia.png" alt="Amelia" />
          <div className="login__brand-divider" />
          <div className="login__brand-text">
            <b>Caster Manager</b>
            <span>RTK Service</span>
          </div>
        </div>

        <div className="login__heroline">
          <h1>Correcciones RTK, transmitidas con precisión geodésica.</h1>
          <p>
            Administra mountpoints, clientes y permisos sobre un caster NTRIP propio.
            Compatible con receptores GNSS de South, Hi-Target, Emlid, Stonex y CHCNAV.
          </p>
        </div>

        <div className="login__hero-foot">
          <span>● NTRIP 2.0 — Caster en línea</span>
          <span>04°42′40″N  074°04′19″W</span>
        </div>
      </div>

      {/* Lado derecho — formulario */}
      <div className="login__form-wrap">
        <form className="login__form" onSubmit={handleSubmit(onSubmit)}>
          <div className="login__form-head">
            <h2>Iniciar sesión</h2>
            <p>Accede al panel del caster Amelia.</p>
          </div>

          <div className="login__form-fields">
            <div className="field">
              <label className="field__label" htmlFor="login-email">Correo</label>
              <input
                id="login-email"
                className="input input--mono"
                type="email"
                autoComplete="email"
                placeholder="admin@amelia.am"
                {...register("email")}
              />
              {errors.email && <div className="field__error">{errors.email.message}</div>}
            </div>
            <div className="field">
              <label className="field__label" htmlFor="login-password">Contraseña</label>
              <input
                id="login-password"
                className="input input--mono"
                type="password"
                autoComplete="current-password"
                {...register("password")}
              />
              {errors.password ? (
                <div className="field__error">{errors.password.message}</div>
              ) : (
                <div className="field__hint">
                  {import.meta.env.VITE_DEMO_MODE === "1"
                    ? "Modo demo: cualquier contraseña funciona."
                    : "Mínimo 8 caracteres."}
                </div>
              )}
            </div>
          </div>

          {serverError && <div className="login__error">{serverError}</div>}

          <button
            type="submit"
            className="btn btn--accent"
            style={{ width: "100%", justifyContent: "center", padding: "11px 14px", fontSize: 13.5 }}
            disabled={submitting}
          >
            {submitting && <span className="spinner" />}
            {submitting ? "Verificando…" : "Entrar al caster"}
            {!submitting && <Icon.Arrow />}
          </button>

          <div className="login__meta">
            <span>v 0.1.0 — caster-manager</span>
            <span>caster.amelia.am:2101</span>
          </div>
        </form>
      </div>
    </div>
  );
}
